import { EnumUserRoles } from 'entities/Users/model/types'
import { type NavTarget } from 'entities/WebMcp/model/navAreas'
import { roleAtLeast } from 'entities/WebMcp/model/roleGating'
import {
  type OnboardingStatus,
  type OnboardingStepKey,
} from 'entities/WebMcp/lib/onboardingSignals'

/**
 * Static pill projection for each onboarding step: how an incomplete step is
 * shown in the empty state and the prompt it sends to the agent when clicked.
 * `minRole` is the lowest role that can perform the step; steps above the user's
 * role are not shown as pills (the co-pilot still escalates if asked directly).
 * Joined to the computed status (onboardingSignals) by `key`.
 */
export interface OnboardingStepPill {
  key: OnboardingStepKey
  pillLabel: string
  pillPrompt: string
  minRole: EnumUserRoles
  targetDestination?: NavTarget
}

export const ONBOARDING_STEP_PILLS: readonly OnboardingStepPill[] = [
  {
    key: 'first_message',
    pillLabel: 'Send your first message',
    pillPrompt: 'Help me send my first message',
    minRole: EnumUserRoles.MEMBER,
    targetDestination: 'Inboxes',
  },
  {
    key: 'first_call',
    pillLabel: 'Make your first call',
    pillPrompt: 'Help me make my first call',
    minRole: EnumUserRoles.MEMBER,
    targetDestination: 'Calls',
  },
  {
    key: 'add_contacts',
    pillLabel: 'Add your contacts',
    pillPrompt: 'Help me add my contacts',
    minRole: EnumUserRoles.MEMBER,
    targetDestination: 'Contacts',
  },
  {
    key: 'webinar',
    pillLabel: 'Join a training webinar',
    pillPrompt: 'Is there a training webinar I can join?',
    minRole: EnumUserRoles.VIEW_ONLY,
  },
  {
    key: 'broadcast',
    pillLabel: 'Schedule a broadcast',
    pillPrompt: 'Help me schedule a broadcast',
    minRole: EnumUserRoles.MEMBER,
    targetDestination: 'Outbound',
  },
  {
    // `textbot` key kept for stored state/signals; the step is now "enable an
    // agent" (seeded default agents exist but are inactive).
    key: 'textbot',
    pillLabel: 'Enable your first AI Agent',
    pillPrompt: 'Help me enable an AI agent',
    minRole: EnumUserRoles.MANAGER,
    targetDestination: 'Textbot',
  },
  {
    key: 'advanced_features',
    pillLabel: 'Explore advanced features',
    pillPrompt: 'Show me some advanced features I might have missed',
    minRole: EnumUserRoles.VIEW_ONLY,
  },
]

/**
 * The onboarding steps still incomplete AND permitted for the role, in checklist
 * order. Shared by the empty-state nudge and the command palette's "Finish setup"
 * group so both draw from the same source. `unknown` and `complete` steps are
 * excluded (only confirmed-incomplete steps are actionable).
 */
export const remainingOnboardingPills = (
  onboarding: OnboardingStatus,
  role: EnumUserRoles
): OnboardingStepPill[] => {
  const statusByKey = new Map(onboarding.steps.map((s) => [s.key, s.status]))
  return ONBOARDING_STEP_PILLS.filter(
    (p) => statusByKey.get(p.key) === 'incomplete' && roleAtLeast(role, p.minRole)
  )
}
